// libs.
import { useSelector } from 'react-redux';
import { Card } from 'react-bootstrap';

// hooks/selectors.
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { getSelectedAlias } from '../../store/slices/domainOrchestratorPricingCommittee.slice';
import { useGetCurrentOrchestratorQuery } from '../../store/services/getOrchestratorPricingCommittee';
import { IWorkflowStep, IWorkflowResponse } from '../../interfaces/interfaces';

// styles.
import { WrapperInProgressStepsCards } from './styles';

interface InProgressCardData {
  stepName: string;
  owner_process: string;
  retry_policy_by: string;
  comment: string;
  created_at: string;
}

const getLastRetriedResponse = (
  responses: IWorkflowResponse[]
): IWorkflowResponse | undefined => {
  for (let i = responses.length - 1; i >= 0; i -= 1) {
    if (responses[i].status === 'RETRIED') return responses[i];
  }
  return undefined;
};

const buildCardFromStep = (step: IWorkflowStep): InProgressCardData | null => {
  const retried = getLastRetriedResponse(step.responses);
  if (!retried) return null;

  const detail = (retried.detail ?? {}) as Record<string, unknown>;

  // Reglas requeridas: comment en response raíz; owner_process y retry_policy_by en detail
  const owner = detail.owner_process as string | undefined;
  const retriedBy = detail.retry_policy_by as string | undefined;
  const comment = retried.comment;

  const hasAll = Boolean(owner) && Boolean(retriedBy) && Boolean(comment);
  if (!hasAll) return null;

  return {
    stepName: step.name,
    owner_process: owner as string,
    retry_policy_by: retriedBy as string,
    comment: comment as string,
    created_at: retried.created_at,
  };
};

interface InProgressStepsCardsProps {
  nid: string;
}

export const InProgressStepsCards = ({ nid }: InProgressStepsCardsProps) => {
  const country = useSelector(getCountry);
  const workflowAlias = useSelector(getSelectedAlias);

  const { data } = useGetCurrentOrchestratorQuery({
    country,
    nid,
    workflow_alias: workflowAlias,
  });

  const steps: IWorkflowStep[] = data?.workflow_steps || [];

  const cards: InProgressCardData[] = steps
    .map(buildCardFromStep)
    .filter(Boolean) as InProgressCardData[];

  if (cards.length === 0) return null;

  return (
    <div data-testid="in-progress-steps-cards" className="in-progress">
      <WrapperInProgressStepsCards>
        {cards.map(card => (
          <Card key={card.stepName}>
            <Card.Body>
              <div className="mb-1 card-sub-title">
                <strong>Fecha: </strong>
                {new Date(card.created_at).toLocaleString()}
              </div>
              <div className="mb-1 card-sub-title">
                <strong>Proceso:</strong> {card.stepName}
              </div>
              <div className="mb-1 card-sub-title">
                <strong>Procesado por:</strong> {card.owner_process}
              </div>
              <div className="mb-1 card-sub-title">
                <strong>Reintentado por:</strong> {card.retry_policy_by}
              </div>
              <div className="mb-1 card-sub-title">
                <strong>Comentario: </strong>
                {card.comment}
              </div>
            </Card.Body>
          </Card>
        ))}
      </WrapperInProgressStepsCards>
    </div>
  );
};

export default InProgressStepsCards;
